import { Component, inject, model, ModelSignal, Signal, signal, WritableSignal } from '@angular/core';
import { form, maxLength, required, submit } from '@angular/forms/signals';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import {
  TASK_CREATE_TITLE_MAX_LENGTH,
  TaskCreateComponent,
  TaskCreateData,
  TaskCreateFormModel,
  toTaskCreateData
} from '@app/components/task/task-create/task-create.component';
import { UiButtonComponent } from '@app/shared/ui/button/ui-button.component';

import { filesMaxSize } from '@app/shared/forms/rules';
import { TasksActionsService } from '@app/shared/services/actions/tasks-actions.service';
import { Location } from '@app/shared/types/models/location/location.model';
import { ProjectIndustry } from '@app/shared/types/models/project-industry/project-industry.model';
import { Project } from '@app/shared/types/models/project/project.model';
import { TaskType } from '@app/shared/types/models/task/task-type.model';
import { Task } from '@app/shared/types/models/task/task.model';
import { UiDialogComponent } from '@app/shared/ui/dialog/ui-dialog.component';
import { TranslatePipe } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
export interface CreateTaskDialogData {
  project: Signal<Project>;
  taskTypes: Signal<TaskType[]>;
  projectIndustries: Signal<ProjectIndustry[]>;
  projectLocations: Signal<Location[]>;
  parentTask?: Task;
  taskPrototype?: Partial<Task>;
}

export interface CreateTaskModalResultData {
  reopenModal: boolean;
}

const MAX_FILES_SIZE_TASK_CREATE_MB = 30;

function toTaskCreateFormModel(taskPrototype: Partial<Task> | undefined): TaskCreateFormModel {
  return {
    taskTypeId: taskPrototype?.taskType()?.id ?? '',
    locationId: taskPrototype?.location()?.id ?? null,
    industryId: taskPrototype?.projectIndustry()?.industry().id ?? null,
    name: '',
    description: '',
    files: []
  };
}

@Component({
  selector: 'proffeo-create-task-dialog',
  templateUrl: './create-task-dialog.component.html',
  providers: [TasksActionsService],
  imports: [MatDialogModule, TranslatePipe, UiDialogComponent, TaskCreateComponent, UiButtonComponent]
})
export class CreateTaskDialogComponent {
  private readonly taskActionsService: TasksActionsService = inject(TasksActionsService);
  private readonly dialogRef: MatDialogRef<CreateTaskDialogComponent> = inject(MatDialogRef<CreateTaskDialogComponent>);

  protected readonly dialogData: CreateTaskDialogData = inject(MAT_DIALOG_DATA);
  protected readonly model: WritableSignal<TaskCreateFormModel> = signal(
    toTaskCreateFormModel(this.dialogData.taskPrototype)
  );
  protected readonly createTaskForm = form(this.model, path => {
    required(path.taskTypeId, { message: 'ui-forms-this-field-is-required' });
    required(path.name, { message: 'project-tasks.create.title-required-error' });
    maxLength(path.name, TASK_CREATE_TITLE_MAX_LENGTH, { message: 'project-tasks.create.title-required-error' });
    filesMaxSize(path.files, MAX_FILES_SIZE_TASK_CREATE_MB, { message: 'ui-forms-files-max-size-exceeded' });
  });

  public firstStep: ModelSignal<boolean> = model(true);

  public save(reopenModal = false): void {
    void submit(this.createTaskForm, {
      action: async () => {
        const taskCreateData: TaskCreateData = toTaskCreateData(this.model());
        if (this.dialogData.parentTask) {
          taskCreateData.task.parentTaskId = this.dialogData.parentTask.id;
        }
        try {
          await firstValueFrom(this.taskActionsService.createNewTask(taskCreateData));
          this.dialogRef.close({ reopenModal });
        } catch {
          // The dialog stays open; the data service already reports the failure.
        }
      }
    });
  }
}
